import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Input, Modal, Select, Spin, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  BusinessContextData,
  CurrencyRate,
  FcDetails,
  Journal,
  useBusinessContext,
} from '../../context/BusinessContext';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import Form from '@sezenta/antd-schema-form';
import { DayJs, Utils } from '../../common/Utils';
import classNames from 'classnames';
import { useConnection, useUser } from '../../connection/connection';
import { useEvents } from '../../common/EventContext';
import OptionedInput, { OptionedInputValue } from '../../common/OptionedInput';
import { Account } from '../../dto';
import CurrencyRateDialog, {
  useCurrencyRateDialog,
} from './CurrencyRateDialog';
import { useCustomRates } from '../../common/CustomRates';

export type JournalModalInstance = {
  newJournal: () => void;
  openJournal: (txId: number) => void;
};

export type JournalTableItem = {
  description?: string;
  siteId?: string;
  accountId?: string;
  debit?: OptionedInputValue;
  credit?: OptionedInputValue;
  fcDetails?: FcDetails;
};

type Props = {};

const getCurrencyForAccount = (
  accounts: Record<string, Account>,
  defaultCcy: string,
  accountId?: string,
): string => {
  return accountId ? (accounts[accountId]?.currency ?? defaultCcy) : defaultCcy;
};

const getOptionedInputValue = (
  defaultCurrency: string,
  isDebit: boolean,
  accounts?: Record<string, Account>,
  value?: any,
  accountId?: string,
) => {
  if (!value?.amount) {
    return {
      option: getCurrencyForAccount(accounts ?? {}, defaultCurrency, accountId),
      input: undefined,
    };
  }
  return {
    option: value.currency ?? defaultCurrency,
    input: (isDebit ? value.amount : -value.amount).toString(),
  };
};

const getFcDetails = (
  business: BusinessContextData,
  record: JournalTableItem,
  rate?: CurrencyRate,
): FcDetails | undefined => {
  if (!record.accountId || (!record.fcDetails?.exchangeRate && !rate?.rate)) {
    return;
  }

  const accounts = business.accounts;
  const defaultCurrency = business.currency;
  const accountCcy = getCurrencyForAccount(
    accounts ?? {},
    defaultCurrency,
    record.accountId,
  );
  if (accountCcy === business.currency) {
    return;
  }

  const sourceCcyAmount = parseFloat(
    record.debit?.input || record.credit?.input || '0',
  );
  const sourceCcy = record.fcDetails?.accountCcy ?? accountCcy;
  const targetCcy = business.currency;
  const exchangeRate = rate?.rate ?? record.fcDetails?.exchangeRate ?? 0.0;

  return {
    accountCcy: sourceCcy,
    receivedCcy: targetCcy,
    accountCcyAmount: sourceCcyAmount,
    receivedCcyAmount: sourceCcyAmount * exchangeRate,
    exchangeRate: exchangeRate,
  };
};

const getBusinessCcyAmountFromRecord = (
  item: JournalTableItem,
  isDebit: boolean,
): number => {
  if (!item.fcDetails)
    return parseFloat(
      (isDebit ? item.debit?.input : item.credit?.input) || '0',
    );
  return (isDebit && item.debit?.input) || (!isDebit && item.credit?.input)
    ? item.fcDetails.receivedCcyAmount
    : 0;
};

const getEmptyTableItem = (defaultCurrency: string): JournalTableItem => ({
  description: '',
  siteId: '',
  accountId: '',
  debit: getOptionedInputValue(defaultCurrency, true),
  credit: getOptionedInputValue(defaultCurrency, false),
  fcDetails: undefined,
});

const JournalModal: ForwardRefRenderFunction<JournalModalInstance, Props> = (
  props,
  ref,
) => {
  const [txId, setTxId] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [data, setData] = useState<JournalTableItem[]>([]);
  const [description, setDescription] = useState<string>();
  const business = useBusinessContext();
  const [form] = Form.useForm();
  const user = useUser();
  const events = useEvents();
  const [saving, setSaving] = useState<boolean>(false);
  const connection = useConnection();
  const currencyRateDgRef = useCurrencyRateDialog();
  const { clearCustomRates, getCustomRate, setCustomRate, hasRateChanged } =
    useCustomRates();

  const updateDataAtIndex = useCallback(
    (
      index: number,
      updateMethod: (record: JournalTableItem) => JournalTableItem,
    ) => {
      setData((prevState) => {
        return prevState.map((a, idx) => {
          if (index === idx) {
            const updated = updateMethod(a);
            return {
              ...updated,
              fcDetails: getFcDetails(business, updated),
            };
          }
          return a;
        });
      });
    },
    [business],
  );

  const getCustomRateOrDefault = useCallback(
    async (
      date: Date,
      sourceCcy: string,
      targetCcy: string,
    ): Promise<CurrencyRate> => {
      const rate = getCustomRate(date, sourceCcy, targetCcy);
      if (rate) {
        return { rate: rate, currencyPair: `${sourceCcy}/${targetCcy}` };
      }
      return await business.getCurrencyRate(sourceCcy, targetCcy, date);
    },
    [business, getCustomRate],
  );

  const updateFcDetailsWithExistingOrDefaultRate = useCallback(async () => {
    for (let i = 0; i < data.length; i++) {
      const record = data[i];
      if (!record.accountId) {
        continue;
      }

      const accountCcy = getCurrencyForAccount(
        business.accounts,
        business.currency,
        record.accountId,
      );
      const ccyRate = await getCustomRateOrDefault(
        new Date(form.getFieldValue('date')),
        accountCcy,
        business.currency,
      );
      if (ccyRate) {
        updateDataAtIndex(i, (record) => {
          return {
            ...record,
            fcDetails: getFcDetails(business, record, ccyRate),
          };
        });
      }
    }
  }, [business, form, data, updateDataAtIndex, getCustomRateOrDefault]);

  const getAmountInBusinessCcy = (record: JournalTableItem) => {
    if (record.debit?.input) {
      return record.fcDetails
        ? (record.fcDetails.receivedCcyAmount ?? 0)
        : parseFloat(record.debit?.input);
    }
    if (record.credit?.input) {
      return -(record.fcDetails
        ? (record.fcDetails.receivedCcyAmount ?? 0)
        : parseFloat(record.credit?.input));
    }
    return 0;
  };

  useImperativeHandle(
    ref,
    () => ({
      newJournal: () => {
        setVisible(true);
        setData([
          getEmptyTableItem(business.currency),
          getEmptyTableItem(business.currency),
        ]);
        clearCustomRates();
        form.resetFields();
        setTxId(undefined);
        form.setFieldsValue({
          date: DayJs().format('YYYY-MM-DD'),
        });
      },
      openJournal: (txId) => {
        setVisible(true);
        setData([
          getEmptyTableItem(business.currency),
          getEmptyTableItem(business.currency),
        ]);
        clearCustomRates();
        form.resetFields();
        setTxId(txId);
        form.setFieldsValue({ date: DayJs().format('YYYY-MM-DD') });
        setLoading(true);
        connection
          .get(`transactions/${business.businessId}/journals/${txId}`)
          .then((res) => {
            const journal: Journal = res.data;
            form.setFieldsValue({
              date: journal.date,
              description: journal.description,
              notes: journal.notes,
            });
            setData(
              journal.items
                .filter((item) => item.txType !== 'intersol')
                .map((item) => {
                  const accountCcy = getCurrencyForAccount(
                    business.accounts,
                    business.currency,
                    item.accountId,
                  );
                  const fcDetails = item.fcDetails;
                  if (fcDetails) {
                    setCustomRate(
                      new Date(journal.date),
                      fcDetails.accountCcy,
                      fcDetails.receivedCcy,
                      fcDetails.exchangeRate,
                      true,
                      false,
                    );
                  }

                  return {
                    siteId: item.siteId,
                    accountId: item.accountId,
                    description: item.description,
                    debit: getOptionedInputValue(
                      accountCcy,
                      true,
                      business.accounts,
                      item.amount > 0
                        ? {
                            currency:
                              item.fcDetails?.accountCcy ?? business.currency,
                            amount:
                              item.fcDetails?.accountCcyAmount ?? item.amount,
                          }
                        : null,
                      item.accountId,
                    ),
                    credit: getOptionedInputValue(
                      accountCcy,
                      false,
                      business.accounts,
                      item.amount < 0
                        ? {
                            currency:
                              item.fcDetails?.accountCcy ?? business.currency,
                            amount:
                              -1 *
                              Math.abs(
                                item.fcDetails?.accountCcyAmount ?? item.amount,
                              ),
                          }
                        : null,
                      item.accountId,
                    ),
                    fcDetails: item.fcDetails,
                  };
                }),
            );
          })
          .finally(() => setLoading(false));
      },
    }),
    [
      business.businessId,
      business.currency,
      business.accounts,
      connection,
      form,
      clearCustomRates,
      setCustomRate,
    ],
  );

  const columns = useMemo(() => {
    return [
      {
        key: 'description',
        title: 'Description',
        dataIndex: 'description',
        render: (text, record, index) => (
          <Input
            placeholder={description ?? 'Write a description'}
            value={text}
            onChange={(e) =>
              setData((prevState) =>
                prevState.map((a, idx) =>
                  index === idx ? { ...a, description: e.target.value } : a,
                ),
              )
            }
          />
        ),
      },
      {
        key: 'accountId',
        title: 'Account',
        dataIndex: 'accountId',
        render: (text, record, index) => (
          <Select
            showSearch={true}
            optionFilterProp="label"
            placeholder="Select the account"
            className="w-[200px]"
            popupClassName="min-w-[400px]"
            options={Object.values(business.accountTypes)
              .map((at) => ({
                label: at.name,
                options: Object.values(business.accounts)
                  .filter((a) => at.accountTypeId === a.accountTypeId)
                  .map((a) => ({ label: a.name, value: a.accountId })),
              }))
              .filter((at) => at.options.length > 0)}
            value={text}
            onChange={async (val) => {
              const accountCcy = getCurrencyForAccount(
                business.accounts,
                business.currency,
                val,
              );
              const ccyRate = await business.getCurrencyRate(
                accountCcy,
                business.currency,
                new Date(form.getFieldValue('date')),
              );

              updateDataAtIndex(index, (record) => {
                const data = {
                  ...record,
                  accountId: val,
                  siteId: business.accounts[val].siteId ?? record.siteId,
                  debit: getOptionedInputValue(
                    business.currency,
                    true,
                    business.accounts,
                    record.debit,
                    val,
                  ),
                  credit: getOptionedInputValue(
                    business.currency,
                    false,
                    business.accounts,
                    record.credit,
                    val,
                  ),
                };
                return {
                  ...data,
                  fcDetails: getFcDetails(business, data, ccyRate),
                };
              });
            }}
          />
        ),
      },
      {
        key: 'siteId',
        title: 'Site',
        dataIndex: 'siteId',
        render: (text, record, index) => {
          return (
            <Select
              showSearch={true}
              optionFilterProp="label"
              placeholder="Select the site"
              className="w-[150px]"
              popupClassName="min-w-[400px]"
              options={[...business.select.sites]}
              value={text}
              disabled={
                record.accountId != null &&
                business.accounts[record.accountId]?.siteId != null
              }
              onChange={(val) =>
                updateDataAtIndex(index, (record) => {
                  return {
                    ...record,
                    siteId: val,
                  };
                })
              }
            />
          );
        },
      },
      {
        key: 'debit',
        title: `Debit`,
        dataIndex: 'debit',
        width: 170,
        render: (item, record, index) => (
          <OptionedInput
            inputType="price"
            readonlyOptions={true}
            values={item}
            options={Object.keys(business.currencies)}
            onChange={(val) =>
              updateDataAtIndex(index, (record) => {
                return {
                  ...record,
                  debit: val,
                  credit: getOptionedInputValue(
                    business.currency,
                    false,
                    business.accounts,
                    null,
                    record.accountId,
                  ),
                };
              })
            }
          />
        ),
      },
      {
        key: 'link',
        dataIndex: 'link',
        className: 'no-padding-cell',
        width: 10,
        render: (item, record, index) => (
          <Button
            type="link"
            onClick={() =>
              updateDataAtIndex(index, (record) => {
                return {
                  ...record,
                  debit: record.debit?.input
                    ? getOptionedInputValue(
                        business.currency,
                        true,
                        business.accounts,
                        null,
                        record.accountId,
                      )
                    : record.credit,
                  credit: record.credit?.input
                    ? getOptionedInputValue(
                        business.currency,
                        false,
                        business.accounts,
                        null,
                        record.accountId,
                      )
                    : record.debit,
                };
              })
            }
          >
            {record.credit?.input ? (
              <ArrowLeftOutlined />
            ) : (
              <ArrowRightOutlined />
            )}
          </Button>
        ),
      },
      {
        key: 'credit',
        title: `Credit`,
        dataIndex: 'credit',
        width: 170,
        render: (item, record, index) => (
          <OptionedInput
            inputType="price"
            readonlyOptions={true}
            values={item}
            options={Object.keys(business.currencies)}
            onChange={(val) => {
              updateDataAtIndex(index, (record) => {
                return {
                  ...record,
                  credit: val,
                  debit: getOptionedInputValue(
                    business.currency,
                    true,
                    business.accounts,
                    null,
                    record.accountId,
                  ),
                };
              });
            }}
          />
        ),
      },
      {
        id: 'actions',
        title: '',
        className: 'no-padding-cell',
        dataIndex: 'description',
        render: (text, record, index) => (
          <div style={{ textAlign: 'right', width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0px',
              }}
            >
              {record.accountId &&
                business.accounts[record.accountId].currency !==
                  business.currency && (
                  <Tooltip title="Edit Currency Rate">
                    <Button
                      type="link"
                      onClick={async () => {
                        const selectedDate = new Date(
                          form.getFieldValue('date'),
                        );
                        const sourceCcy = getCurrencyForAccount(
                          business.accounts,
                          business.currency,
                          record.accountId,
                        );
                        const targetCcy = business.currency;
                        const rate = (
                          await getCustomRateOrDefault(
                            selectedDate,
                            sourceCcy,
                            targetCcy,
                          )
                        )?.rate;
                        const isCredit = record.credit?.input?.trim()
                          ? true
                          : false;

                        currencyRateDgRef.current?.open(
                          rate,
                          sourceCcy,
                          targetCcy,
                          parseFloat(
                            (record.debit?.input || record.credit?.input) ??
                              '0',
                          ),
                          (rate, sourceCcyAmount) => {
                            setCustomRate(
                              selectedDate,
                              sourceCcy,
                              targetCcy,
                              rate,
                              true,
                            );
                            updateDataAtIndex(index, (record) => {
                              return {
                                ...record,
                                debit: {
                                  option: sourceCcy,
                                  input: isCredit
                                    ? undefined
                                    : sourceCcyAmount.toString(),
                                },
                                credit: {
                                  option: sourceCcy,
                                  input: isCredit
                                    ? sourceCcyAmount.toString()
                                    : undefined,
                                },
                                fcDetails: getFcDetails(business, record, {
                                  rate: rate,
                                  currencyPair: `${sourceCcy}/${targetCcy}`,
                                }),
                              };
                            });
                          },
                        );
                      }}
                    >
                      <EditOutlined />
                    </Button>
                  </Tooltip>
                )}
              <Button
                type="link"
                onClick={() =>
                  setData((prevState) =>
                    prevState.filter((_, idx) => idx !== index),
                  )
                }
              >
                <DeleteOutlined />
              </Button>
            </div>
          </div>
        ),
      },
    ] as ColumnsType<JournalTableItem>;
  }, [
    business,
    description,
    currencyRateDgRef,
    form,
    updateDataAtIndex,
    setCustomRate,
    getCustomRateOrDefault,
  ]);

  const [credit, debit] = useMemo(() => {
    return [
      data
        .map((d) => getBusinessCcyAmountFromRecord(d, false))
        .reduce((a, b) => a + b, 0),
      data
        .map((d) => getBusinessCcyAmountFromRecord(d, true))
        .reduce((a, b) => a + b, 0),
    ];
  }, [data]);

  const doesCreditMatchDebit = useCallback(() => {
    return Math.abs(credit - debit) < 0.01;
  }, [credit, debit]);

  const submit = useCallback(
    (values: any) => {
      for (const d of data) {
        if (!d.accountId) {
          Modal.error({
            title: 'Validation failed',
            content: 'Please set account of all records',
          });
          return;
        }
        if (!d.siteId) {
          Modal.error({
            title: 'Validation failed',
            content: 'Please set site of all records',
          });
          return;
        }
        if (!d.debit && !d.credit) {
          Modal.error({
            title: 'Validation failed',
            content: 'Please set amount of all records',
          });
          return;
        }
      }

      setSaving(true);
      const payload = {
        businessId: business.businessId,
        date: values.date,
        type: values.type,
        category: values.category,
        notes: values.notes,
        description: values.description,
        userId: user.userId,
        items: data.map((d) => ({
          accountId: d.accountId,
          siteId: d.siteId,
          description: d.description ?? values.description,
          txType: 'journal',
          amount: getAmountInBusinessCcy(d),
          otherSide: 'journal',
          fcDetails: d.fcDetails,
        })),
      };
      (txId
        ? connection.patch(
            `transactions/${business.businessId}/journals/${txId}`,
            payload,
          )
        : connection.post(
            `transactions/${business.businessId}/journals`,
            payload,
          )
      )
        .then((res) => {
          setVisible(false);
          events.emit('TRANSACTIONS', {
            action: 'CREATE',
            data: res.data.transactions,
          });
          events.emit('ACTIVITIES', {
            action: 'CREATE',
            data: res.data.activities,
          });
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [business.businessId, connection, data, events, txId, user.userId],
  );
  const deleteTx = useCallback(() => {
    setSaving(true);
    connection
      .delete(`transactions/${business.businessId}/journals/${txId}`)
      .then((res) => {
        setVisible(false);
        events.emit('TRANSACTIONS', {
          action: 'DELETE',
          data: txId,
        });
        events.emit('ACTIVITIES', {
          action: 'DELETE',
          data: txId,
        });
      })
      .finally(() => {
        setSaving(false);
      });
  }, [business.businessId, connection, events, txId]);

  return (
    <Modal
      centered
      open={visible}
      onCancel={() => setVisible(false)}
      title="Journal Entry"
      width={996}
      confirmLoading={saving}
      footer={
        <div className="flex justify-between items-center">
          <div>
            <Button
              loading={saving || loading}
              danger={true}
              icon={<DeleteOutlined />}
              disabled={!txId}
              onClick={() =>
                Modal.confirm({
                  title: 'Delete Journal Entry',
                  content:
                    'Are you sure you want to delete this journal entry?',
                  okButtonProps: { danger: true },
                  onOk: deleteTx,
                })
              }
            >
              Delete
            </Button>{' '}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={saving || loading}
              onClick={() => form.submit()}
              disabled={
                !doesCreditMatchDebit() ||
                credit === 0 ||
                data.findIndex(
                  (a) => a.accountId === undefined || a.siteId === undefined,
                ) !== -1
              }
            >
              {txId ? 'Update' : 'Create'} Journal Entry
            </Button>
          </div>
        </div>
      }
      onOk={() => form.submit()}
      okText="Create Journal Entry"
    >
      <CurrencyRateDialog ref={currencyRateDgRef} />
      <Spin spinning={loading}>
        <div className="flex flex-col gap-2">
          <Form
            form={form}
            layout="vertical"
            onFinish={submit}
            onValuesChange={(changedValues) => {
              if (Object.keys(changedValues)[0] === 'description') {
                setDescription(changedValues.description);
              }
            }}
          >
            <div className="flex gap-2 w-full">
              <Form.Items
                schema={[
                  {
                    id: 'date',
                    name: 'Date',
                    type: 'date',
                    options: {
                      rules: [{ required: true, message: 'Date is required' }],
                    },
                    props: {
                      onChange: (date: any) => {
                        setLoading(true);
                        business
                          .getCurrencyRate('', '', new Date(date))
                          .then(() => {
                            setLoading(false);
                            updateFcDetailsWithExistingOrDefaultRate();
                          });
                      },
                    },
                  },
                ]}
              />
              <div className="grow shrink">
                <Form.Items
                  schema={[
                    {
                      id: 'description',
                      name: 'Description',
                      type: 'string',
                      props: { placeholder: 'Write a description' },
                      options: {
                        rules: [
                          {
                            required: true,
                            message: 'Description is required',
                          },
                        ],
                      },
                    },
                  ]}
                />
              </div>
              {/*<Space size={0} direction="vertical">*/}
              {/*  <label>Date</label>*/}
              {/*  <DatePicker />*/}
              {/*</Space>*/}
              {/*<Space size={0} direction="vertical" className="grow shrink">*/}
              {/*  <label>Description</label>*/}
              {/*  <Input placeholder="Write a description" />*/}
              {/*</Space>*/}
            </div>
          </Form>
          <Table<JournalTableItem>
            columns={columns}
            dataSource={data}
            pagination={false}
          />
          <div className="flex justify-between gap-2">
            <div className="pl-4">
              <Button
                onClick={() =>
                  setData((prevState) => [
                    ...prevState,
                    getEmptyTableItem(business.currency),
                  ])
                }
              >
                Add Line
              </Button>
            </div>
            <div>
              <table>
                <tr>
                  <td className="text-right">Total debits</td>
                  <td></td>
                  <td>Total credits</td>
                </tr>
                <tr className="text-xl font-bold">
                  <td className="text-right">
                    {business.currency} {Utils.formatPrice(debit)}
                  </td>
                  <td>{doesCreditMatchDebit() ? '=' : '≠'}</td>
                  <td>
                    {business.currency} {Utils.formatPrice(credit)}
                  </td>
                </tr>
                <tr className="text-xl font-medium">
                  <td className="text-right">Difference</td>
                  <td>=</td>
                  <td
                    className={classNames({
                      'text-green-700': doesCreditMatchDebit(),
                      'text-red-700': !doesCreditMatchDebit(),
                    })}
                  >
                    LKR {Utils.formatPrice(Math.abs(credit - debit))}
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export const useJournalModal = () => useRef<JournalModalInstance>(null);

export default React.forwardRef<JournalModalInstance, Props>(JournalModal);
